// Pod hotspot — compare magnitude across pods.
// Pods have NO natural order, so every bar is slot 1. Colouring bars darker-where-
// bigger would double-encode length as hue and is an explicit anti-pattern.
import { useState } from 'react';
import { useChart, tooltipStyle, axisStyle } from '../lib/useChart';
import { chrome, seriesOne } from '../lib/palette';
import { esc } from '../lib/format';
import ChartCard from './ChartCard';

const UNOWNED = 'unowned';

export default function PodHotspot({ pods, mode }) {
  const [showTable, setShowTable] = useState(false);
  const c = chrome(mode);
  const color = seriesOne(mode);
  // Hooks must run unconditionally — switching latest (has rows) → older fixture
  // runs (empty) used to early-return before useChart and blank the whole app.
  const empty = !pods?.length;

  // Bars read bottom-up in ECharts, so reverse for "worst at the top".
  const rows = empty ? [] : [...pods].reverse();
  const labels = rows.map((p) => p.owner_pod || UNOWNED);

  const [ref] = useChart(
    () => ({
      backgroundColor: 'transparent',
      grid: { left: 4, right: 40, top: 8, bottom: 24, containLabel: true },
      tooltip: {
        trigger: 'item',
        ...tooltipStyle(c),
        formatter: (p) => {
          const row = rows[p.dataIndex];
          return (
            `<div style="font-weight:600;font-size:14px">${esc(row.tests_blocked)} tests blocked</div>` +
            `<div style="color:${c.textSecondary}">${esc(row.owner_pod || UNOWNED)} · ` +
            `${esc(row.clusters)} signal${row.clusters === 1 ? '' : 's'} · top impact ${esc(row.top_impact)}</div>`
          );
        },
      },
      xAxis: { type: 'value', ...axisStyle(c), axisLine: { show: false }, axisLabel: { show: false }, splitLine: { show: false } },
      yAxis: {
        type: 'category',
        data: labels,
        ...axisStyle(c),
        splitLine: { show: false },
        axisLabel: { color: c.textSecondary, fontSize: 12 },
      },
      series: [
        {
          type: 'bar',
          data: rows.map((p) => p.tests_blocked),
          barMaxWidth: 24,
          // 4px rounded data-end, square at the baseline.
          itemStyle: { color, borderRadius: [0, 4, 4, 0] },
          // Value at the tip — outside the bar, so it can never be clipped.
          label: { show: true, position: 'right', color: c.textPrimary, fontSize: 12, fontWeight: 600 },
          // The hovered bar lifts and its neighbours recede, so the pointer gets
          // visible feedback rather than only a floating tooltip.
          emphasis: { focus: 'self', itemStyle: { borderColor: c.textPrimary, borderWidth: 1.5 } },
          blur: { itemStyle: { opacity: 0.4 } },
        },
      ],
    }),
    [pods],
    { mode }
  );

  // An empty plot area reads as broken. Say there is nothing, don't draw nothing.
  // useChart's effect no-ops when the chart div is not mounted (ref.current is null).
  if (empty) {
    return (
      <ChartCard title="Pod hotspots" subtitle="Nothing blocked in this scope">
        <p className="chart-empty">No pod is currently blocked.</p>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="Pod hotspots"
      subtitle="Tests blocked by owning pod"
      showTable={showTable}
      onToggleTable={() => setShowTable((v) => !v)}
      table={
        <table className="data-table">
          <thead>
            <tr><th>Pod</th><th className="num">Tests blocked</th><th className="num">Signals</th><th className="num">Top impact</th></tr>
          </thead>
          <tbody>
            {pods.map((p) => (
              <tr key={p.owner_pod || UNOWNED}>
                <td>{p.owner_pod || UNOWNED}</td>
                <td className="num">{p.tests_blocked}</td>
                <td className="num">{p.clusters}</td>
                <td className="num">{p.top_impact}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div ref={ref} className="chart" style={{ height: `${Math.max(120, pods.length * 38 + 32)}px` }} />
    </ChartCard>
  );
}
